import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { TICKET_CATEGORIES } from '../../components/CreateTicketDialog';
import { ArrowLeft, Send, ArrowUpCircle, Settings2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  open: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-600',
};
const PRIORITY_STYLES = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-rose-100 text-rose-700',
};
const CAT_LABEL = Object.fromEntries(TICKET_CATEGORIES.map((c) => [c.value, c.label]));
const fmt = (s) => (s || '').replace(/_/g, ' ');

const TicketDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/${id}`);
      setTicket(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ticket');
      if ([403, 404].includes(e.response?.status)) navigate('/staff/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    axios.get(`${API}/tickets/assignees`).then((r) => setAssignees(r.data)).catch(() => {});
  }, [load]);

  if (loading) return <div className="text-center py-20 text-slate-600">Loading...</div>;
  if (!ticket) return null;

  const canEdit = ['director', 'admin', 'senior_executive'].includes(user?.role) || ticket.assigned_to === user?.id ||
    (user?.role === 'finance' && ticket.category === 'payment_issue');
  const canEscalate = ['executive', 'senior_executive'].includes(user?.role);

  const patch = async (payload, msg) => {
    try {
      await axios.put(`${API}/tickets/${id}`, payload);
      toast.success(msg || 'Updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      await axios.post(`${API}/tickets/${id}/comments`, { text: comment });
      setComment('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to comment');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="ticket-detail-page">
      <Button variant="ghost" onClick={() => navigate('/staff/tickets')} className="mb-4" data-testid="back-to-tickets">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Tickets
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className="text-xs font-mono text-slate-400">{ticket.ticket_number}</span>
                  <h1 className="text-2xl font-bold text-slate-900" data-testid="ticket-title">{ticket.title}</h1>
                </div>
                <div className="flex gap-2">
                  <Badge className={PRIORITY_STYLES[ticket.priority]}>{ticket.priority}</Badge>
                  <Badge className={STATUS_STYLES[ticket.status]} data-testid="ticket-status-badge">{fmt(ticket.status)}</Badge>
                </div>
              </div>
              {ticket.sla_breached && (
                <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700 font-medium" data-testid="ticket-sla-banner">
                  SLA breached — open for {Math.floor(ticket.age_hours)}h (target {ticket.sla_hours}h). Needs immediate action.
                </div>
              )}
              <p className="text-slate-700 mt-4 whitespace-pre-wrap">{ticket.description}</p>
              {ticket.resolution_notes && (
                <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div className="text-sm font-semibold text-emerald-700 mb-1">Resolution Notes</div>
                  <p className="text-sm text-emerald-800 whitespace-pre-wrap">{ticket.resolution_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-indigo-600" />Comments Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {(ticket.comments || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No activity yet.</p>
                ) : (
                  ticket.comments.map((c) => (
                    <div key={c.id} className={`p-3 rounded-lg ${c.type === 'system' ? 'bg-slate-50 border border-dashed border-slate-200' : 'bg-indigo-50'}`} data-testid={`comment-${c.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-800">{c.author_name} <span className="text-xs text-slate-400">({fmt(c.author_role)})</span></span>
                        <span className="text-xs text-slate-400">{(c.created_at || '').replace('T', ' ').slice(0, 16)}</span>
                      </div>
                      <p className={`text-sm mt-1 ${c.type === 'system' ? 'text-slate-500 italic' : 'text-slate-700'}`}>{c.text}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." onKeyDown={(e) => e.key === 'Enter' && postComment()} data-testid="comment-input" />
                <Button onClick={postComment} data-testid="comment-submit"><Send className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-0 shadow-lg">
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Category" value={CAT_LABEL[ticket.category]} />
              <Row label="Raised by" value={`${ticket.raised_by_name} (${fmt(ticket.raised_by_role)})`} />
              <Row label="Assigned to" value={ticket.assigned_to_name || 'Unassigned'} />
              {ticket.student_name && <Row label="Student" value={ticket.student_name} />}
              {ticket.teacher_name && <Row label="Teacher" value={ticket.teacher_name} />}
              {ticket.complaint_against && <Row label="Against" value={fmt(ticket.complaint_against)} />}
              {ticket.escalation_level > 0 && <Row label="Escalated to" value={fmt(ticket.escalated_to_role)} />}
            </CardContent>
          </Card>

          {canEdit && (
            <Card className="border-0 shadow-lg">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings2 className="w-4 h-4" />Manage</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={ticket.status} onValueChange={(v) => patch({ status: v }, 'Status updated')}>
                    <SelectTrigger data-testid="manage-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{['open', 'in_progress', 'resolved', 'closed'].map((s) => <SelectItem key={s} value={s}>{fmt(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={ticket.priority} onValueChange={(v) => patch({ priority: v }, 'Priority updated')}>
                    <SelectTrigger data-testid="manage-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Assign To</Label>
                  <Select value={ticket.assigned_to || 'none'} onValueChange={(v) => patch({ assigned_to: v === 'none' ? null : v }, 'Reassigned')}>
                    <SelectTrigger data-testid="manage-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({fmt(a.role)})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <ResolutionDialog ticket={ticket} onSave={(notes) => patch({ resolution_notes: notes, status: 'resolved' }, 'Resolved')} />
              </CardContent>
            </Card>
          )}

          {canEscalate && (
            <EscalateDialog ticketId={id} userRole={user.role} assignees={assignees} onDone={load} />
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium text-right">{value}</span>
  </div>
);

const ResolutionDialog = ({ ticket, onSave }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(ticket.resolution_notes || '');
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="resolve-button">Add Resolution & Resolve</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Resolve Ticket</DialogTitle><DialogDescription>Add resolution notes and mark this ticket resolved.</DialogDescription></DialogHeader>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder="How was this resolved?" data-testid="resolution-notes-input" />
        <DialogFooter>
          <Button onClick={() => { onSave(notes); setOpen(false); }} data-testid="resolution-save-button">Save & Resolve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EscalateDialog = ({ ticketId, userRole, assignees, onDone }) => {
  const [open, setOpen] = useState(false);
  const targets = userRole === 'executive' ? ['senior_executive', 'director'] : ['director'];
  const [form, setForm] = useState({ escalate_to_role: targets[0], assigned_to: 'auto', note: '' });
  const submit = async () => {
    try {
      await axios.post(`${API}/tickets/${ticketId}/escalate`, {
        escalate_to_role: form.escalate_to_role,
        assigned_to: form.assigned_to === 'auto' ? null : form.assigned_to,
        note: form.note || null,
      });
      toast.success('Ticket escalated');
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to escalate');
    }
  };
  const targetUsers = assignees.filter((a) => a.role === form.escalate_to_role);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" className="w-full bg-rose-50 text-rose-700 hover:bg-rose-100" data-testid="escalate-button">
          <ArrowUpCircle className="w-4 h-4 mr-2" />Escalate
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Escalate Ticket</DialogTitle><DialogDescription>Escalate this ticket up the chain.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Escalate To</Label>
            <Select value={form.escalate_to_role} onValueChange={(v) => setForm({ ...form, escalate_to_role: v, assigned_to: 'auto' })}>
              <SelectTrigger data-testid="escalate-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{targets.map((t) => <SelectItem key={t} value={t}>{fmt(t)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Specific Person (optional)</Label>
            <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
              <SelectTrigger data-testid="escalate-person-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-assign</SelectItem>
                {targetUsers.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="escalate-note-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="escalate-submit-button">Escalate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetail;
